import fetch from "cross-fetch";
import { EventSourcePolyfill } from "event-source-polyfill";
import type { components } from "./types/ctrl";
import type {
  CreateEmulatorRequest,
  CreateEmulatorResponse,
  DeleteEmulatorResponse,
  PromoteSnapshotRootRequest,
} from "./types";

export interface CtrlApiConfig {
  baseURL: string;
  authentication?: {
    type: "bearer" | "basic" | "apiKey";
    token?: string;
    username?: string;
    password?: string;
    apiKey?: string;
    apiKeyHeader?: string;
  };
  headers?: Record<string, string>;
  timeout?: number;
  /**
   * Called when ctrl answers 401 to a request that carried a bearer token,
   * with that token, so the app can renew or end the session. Without it a
   * dead token keeps getting sent by every poller.
   */
  onUnauthorized?: (token: string) => void;
}

/**
 * Local alias for `encodeURIComponent`, applied to every value
 * interpolated into a URL path segment to prevent path traversal
 * and ambiguity when an id contains `/`, `?`, `#`, or other reserved
 * characters. Use for path segments only — query string values are
 * encoded automatically by `URLSearchParams` in `buildURL`.
 */
const enc = encodeURIComponent;

export type SettingScope = NonNullable<components["schemas"]["OpenapigenSettingOverride"]["scope_type"]>;

const SCOPE_COLLECTION: Record<Exclude<SettingScope, "system">, string> = {
  tenant: "tenants",
  group: "groups",
  user: "users",
};

function settingsScopePath(scope: SettingScope, id?: string): string {
  if (scope === "system") return "/api/v1/settings/system";
  if (!id) throw new Error(`${scope} settings need an id`);
  return `/api/v1/${SCOPE_COLLECTION[scope]}/${enc(id)}/settings`;
}

type CtrlApiErrorResponse = { message: string };
export class CtrlApiError<
  T extends CtrlApiErrorResponse = CtrlApiErrorResponse,
> extends Error {
  public readonly status: number;
  public readonly data: T;
  public readonly statusText: string;

  constructor(status: number, statusText: string, data?: T) {
    const message = data
      ? JSON.stringify(data)
      : `HTTP ${status}: ${statusText}`;
    super(message);
    this.name = "CtrlApiError";
    this.status = status;
    this.statusText = statusText;
    this.data = data as T;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CtrlApiError);
    }
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  headers?: Record<string, string>;
  body?: unknown;
  params?: Record<string, string | number | boolean>;
}

/**
 * Payloads of the bootstrap event stream (`GET /tenants/{id}/bootstrap/events`).
 * The OpenAPI schema types the stream only as text/event-stream, so these
 * mirror ctrl's `BootstrapStream*` types in pkg/api/bootstrap_events.go.
 */
export type BootstrapRunStatus = "pending" | "running" | "completed" | "failed";

/** `operation` event: the run the stream follows. */
export interface BootstrapStreamOperation {
  operation_id: string;
  status: BootstrapRunStatus;
}

/** `log` event: one step of the run. `id` increases within an operation. */
export interface BootstrapStreamEvent {
  id: number;
  time: string;
  phase: string;
  level: "info" | "warn" | "error";
  message: string;
}

/** `end` event: the run finished and the server closes the stream. */
export interface BootstrapStreamEnd {
  operation_id: string;
  status: Extract<BootstrapRunStatus, "completed" | "failed">;
  error?: string;
}

export interface EmulatorLogsOptions {
  follow?: boolean;
  timestamps?: boolean;
  previous?: boolean;
  sinceSeconds?: number;
  sinceTime?: string;
  limitBytes?: number;
  tailLines?: number;
}

export class CtrlApiClient {
  private config: CtrlApiConfig;

  constructor(config: CtrlApiConfig) {
    this.config = config;
  }

  get token() {
    return this.config?.authentication?.token;
  }

  getAuthHeaders(): Record<string, string> {
    const { authentication } = this.config;
    if (!authentication) return {};

    switch (authentication.type) {
      case "bearer":
        return authentication.token
          ? { Authorization: `Bearer ${authentication.token}` }
          : {};
      case "basic":
        if (authentication.username && authentication.password) {
          const encoded = btoa(
            `${authentication.username}:${authentication.password}`,
          );
          return { Authorization: `Basic ${encoded}` };
        }
        return {};
      case "apiKey":
        return authentication.apiKey && authentication.apiKeyHeader
          ? { [authentication.apiKeyHeader]: authentication.apiKey }
          : {};
      default:
        return {};
    }
  }

  public buildURL(
    path: string,
    params?: Record<string, string | number | boolean>,
  ): string {
    const url = new URL(path, this.config.baseURL);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }
    return url.toString();
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = "GET", headers = {}, body, params } = options;

    const url = this.buildURL(path, params);

    const authHeaders = this.getAuthHeaders();
    const requestHeaders = {
      "Content-Type": "application/json",
      ...this.config.headers,
      ...authHeaders,
      ...headers,
    };

    const controller = new AbortController();
    const timeoutId = this.config.timeout
      ? setTimeout(() => controller.abort(), this.config.timeout)
      : null;

    try {
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      if (timeoutId) clearTimeout(timeoutId);

      if (!response.ok) {
        const { authentication, onUnauthorized } = this.config;
        if (
          response.status === 401 &&
          authentication?.type === "bearer" &&
          authentication.token
        ) {
          try {
            onUnauthorized?.(authentication.token);
          } catch {
            // A failing app callback must not mask the 401 for the caller.
          }
        }

        let errorData: any;

        try {
          errorData = await response.json();
        } catch {
          // If JSON parsing fails, errorData will remain undefined
        }

        throw new CtrlApiError(response.status, response.statusText, errorData);
      }

      // Handle empty responses (e.g., 204 No Content)
      const contentType = response.headers.get("content-type");
      const contentLength = response.headers.get("content-length");

      if (
        response.status === 204 ||
        contentLength === "0" ||
        !contentType?.includes("application/json")
      ) {
        return (await response.text()) as T;
      }

      return await response.json();
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      throw error;
    }
  }

  // API Methods based on OpenAPI schema
  async getEmulators(): Promise<components["schemas"]["K8SEmulator"][]> {
    return this.request<components["schemas"]["K8SEmulator"][]>(
      "/api/v1/emulators",
    );
  }

  async getEmulator(id: string): Promise<components["schemas"]["K8SEmulator"]> {
    return this.request<components["schemas"]["K8SEmulator"]>(
      `/api/v1/emulators/${enc(id)}`,
    );
  }

  getEmulatorLogs(
    id: string,
    options?: EmulatorLogsOptions,
  ): EventSourcePolyfill {
    const url = this.buildURL(
      `/api/v1/emulators/${enc(id)}/logs`,
      options as Record<string, string | number | boolean>,
    );

    const authHeaders = this.getAuthHeaders();
    const requestHeaders = {
      ...this.config.headers,
      ...authHeaders,
    };

    return new EventSourcePolyfill(url, {
      headers: requestHeaders,
    });
  }

  /**
   * Stream provisioning progress over SSE (`progress`, `event`, `end`, `error`
   * events). EventSourcePolyfill so the Authorization header rides along.
   */
  getEmulatorEvents(id: string): EventSourcePolyfill {
    const url = this.buildURL(`/api/v1/emulators/${enc(id)}/events`);

    const authHeaders = this.getAuthHeaders();
    const requestHeaders = {
      ...this.config.headers,
      ...authHeaders,
    };

    return new EventSourcePolyfill(url, {
      headers: requestHeaders,
    });
  }

  /**
   * Stream a tenant's bootstrap log over SSE (`ready`, `operation`, `log`,
   * `end`, `error`). POST /bootstrap blocks until the run ends, so open this
   * first, wait for `ready`, then start the run: the stream follows the run
   * created after it connected (or resumes one in flight; `after` skips log
   * ids already seen). EventSourcePolyfill so the Authorization header rides
   * along.
   */
  streamBootstrapEvents(tenantId: string, after?: number): EventSourcePolyfill {
    const url = this.buildURL(
      `/api/v1/tenants/${enc(tenantId)}/bootstrap/events`,
      after ? { after } : undefined,
    );

    const authHeaders = this.getAuthHeaders();
    const requestHeaders = {
      ...this.config.headers,
      ...authHeaders,
    };

    return new EventSourcePolyfill(url, {
      headers: requestHeaders,
    });
  }

  async restartEmulator(
    id: string,
  ): Promise<components["schemas"]["OpenapigenRestartEmulatorResponse"]> {
    return this.request<
      components["schemas"]["OpenapigenRestartEmulatorResponse"]
    >(`/api/v1/emulators/${enc(id)}/restart`, {
      method: "POST",
    });
  }

  async createEmulator(
    params: CreateEmulatorRequest,
  ): Promise<CreateEmulatorResponse> {
    return this.request<CreateEmulatorResponse>("/api/v1/emulators", {
      method: "POST",
      body: params,
    });
  }

  async deleteEmulator(id: string): Promise<DeleteEmulatorResponse> {
    return this.request<DeleteEmulatorResponse>(`/api/v1/emulators/${enc(id)}`, {
      method: "DELETE",
    });
  }

  // Scenarios (snapshot-carried VHAL test scenarios, executed server-side)

  async listScenarios(
    id: string,
  ): Promise<components["schemas"]["OpenapigenEmulatorScenarioMeta"][]> {
    return this.request<components["schemas"]["OpenapigenEmulatorScenarioMeta"][]>(
      `/api/v1/emulators/${enc(id)}/scenarios`,
    );
  }

  async applyScenario(
    id: string,
    scenarioId: string,
  ): Promise<components["schemas"]["OpenapigenApplyEmulatorScenarioResponse"]> {
    return this.request<
      components["schemas"]["OpenapigenApplyEmulatorScenarioResponse"]
    >(`/api/v1/emulators/${enc(id)}/scenarios/${enc(scenarioId)}/apply`, {
      method: "POST",
    });
  }

  async cancelScenarioRun(
    id: string,
    runId: string,
  ): Promise<components["schemas"]["OpenapigenCancelEmulatorScenarioRunResponse"]> {
    return this.request<
      components["schemas"]["OpenapigenCancelEmulatorScenarioRunResponse"]
    >(`/api/v1/emulators/${enc(id)}/scenario-runs/${enc(runId)}/cancel`, {
      method: "POST",
    });
  }

  /**
   * Stream scenario run progress over SSE. Uses EventSourcePolyfill so the
   * Authorization header rides along (native EventSource cannot set headers).
   */
  getScenarioRun(id: string, runId: string): EventSourcePolyfill {
    const url = this.buildURL(
      `/api/v1/emulators/${enc(id)}/scenario-runs/${enc(runId)}`,
    );

    const authHeaders = this.getAuthHeaders();
    const requestHeaders = {
      ...this.config.headers,
      ...authHeaders,
    };

    return new EventSourcePolyfill(url, {
      headers: requestHeaders,
    });
  }

  async getSnapshots(): Promise<components["schemas"]["K8SSnapshot"][]> {
    return this.request<components["schemas"]["K8SSnapshot"][]>(
      "/api/v1/snapshots",
    );
  }

  async getSnapshotsTree(): Promise<
    components["schemas"]["K8SSnapshotNode"][]
  > {
    const res = await this.request<
      components["schemas"]["OpenapigenSnapshotTreeResponse"]
    >("/api/v1/snapshots/tree");
    return res.tree ?? [];
  }

  async getSnapshot(
    name: string,
  ): Promise<components["schemas"]["K8SSnapshot"]> {
    return this.request<components["schemas"]["K8SSnapshot"]>(
      `/api/v1/snapshots/${enc(name)}`,
    );
  }

  async getDiskPools(): Promise<components["schemas"]["K8SDiskPool"][]> {
    return this.request<components["schemas"]["K8SDiskPool"][]>(
      "/api/v1/snapshots/diskpool",
    );
  }

  async deleteSnapshot(name: string): Promise<string> {
    return this.request<string>(`/api/v1/snapshots/${enc(name)}`, {
      method: "DELETE",
    });
  }

  /**
   * Promote a snapshot to a retention root. On success returns the updated
   * snapshot. Throws `CtrlApiError<{ message, problems }>` with status 409
   * when the snapshot isn't safe to promote — retry with `force: true` in
   * `params` to override.
   */
  async promoteSnapshotRoot(
    name: string,
    params: PromoteSnapshotRootRequest,
  ): Promise<components["schemas"]["K8SSnapshot"]> {
    return this.request<components["schemas"]["K8SSnapshot"]>(
      `/api/v1/snapshots/${enc(name)}/promote-root`,
      { method: "POST", body: params },
    );
  }

  /** Demote a snapshot, removing its root labels. Returns the updated snapshot. */
  async demoteSnapshotRoot(
    name: string,
  ): Promise<components["schemas"]["K8SSnapshot"]> {
    return this.request<components["schemas"]["K8SSnapshot"]>(
      `/api/v1/snapshots/${enc(name)}/demote-root`,
      { method: "POST" },
    );
  }

  // Applications
  async getApplications(): Promise<
    components["schemas"]["DbListApplicationsRow"][]
  > {
    return this.request<components["schemas"]["DbListApplicationsRow"][]>(
      "/api/v1/applications",
    );
  }

  async getApplication(
    id: string,
  ): Promise<components["schemas"]["DbGetApplicationRow"]> {
    return this.request<components["schemas"]["DbGetApplicationRow"]>(
      `/api/v1/applications/${enc(id)}`,
    );
  }

  async createApplication(
    params: components["schemas"]["DbCreateApplicationParams"],
  ): Promise<components["schemas"]["DbGetApplicationRow"]> {
    return this.request<components["schemas"]["DbGetApplicationRow"]>(
      "/api/v1/applications",
      {
        method: "POST",
        body: params,
      },
    );
  }

  async updateApplication(
    id: string,
  ): Promise<components["schemas"]["DbGetApplicationRow"]> {
    return this.request<components["schemas"]["DbGetApplicationRow"]>(
      `/api/v1/applications/${enc(id)}`,
      {
        method: "PUT",
      },
    );
  }

  async deleteApplication(id: string): Promise<string> {
    return this.request<string>(`/api/v1/applications/${enc(id)}`, {
      method: "DELETE",
    });
  }

  async getApplicationTargets(
    id: string,
  ): Promise<components["schemas"]["DbGetTargetsForApplicationRow"][]> {
    return this.request<
      components["schemas"]["DbGetTargetsForApplicationRow"][]
    >(`/api/v1/applications/${enc(id)}/targets`);
  }

  async addApplicationTargets(
    id: string,
  ): Promise<components["schemas"]["DbGetTargetsForApplicationRow"][]> {
    return this.request<
      components["schemas"]["DbGetTargetsForApplicationRow"][]
    >(`/api/v1/applications/${enc(id)}/targets`, {
      method: "POST",
    });
  }

  async removeApplicationTarget(id: string, targetId: string): Promise<string> {
    return this.request<string>(
      `/api/v1/applications/${enc(id)}/targets/${enc(targetId)}`,
      {
        method: "DELETE",
      },
    );
  }

  // Endpoints
  async getEndpoints(): Promise<components["schemas"]["DbListEndpointsRow"][]> {
    return this.request<components["schemas"]["DbListEndpointsRow"][]>(
      "/api/v1/endpoints",
    );
  }

  async getEndpoint(
    id: string,
  ): Promise<components["schemas"]["DbGetEndpointRow"]> {
    return this.request<components["schemas"]["DbGetEndpointRow"]>(
      `/api/v1/endpoints/${enc(id)}`,
    );
  }

  async createEndpoint(
    params: components["schemas"]["DbCreateEndpointParams"],
  ): Promise<components["schemas"]["DbGetEndpointRow"]> {
    return this.request<components["schemas"]["DbGetEndpointRow"]>(
      "/api/v1/endpoints",
      {
        method: "POST",
        body: params,
      },
    );
  }

  async updateEndpoint(
    id: string,
  ): Promise<components["schemas"]["DbGetEndpointRow"]> {
    return this.request<components["schemas"]["DbGetEndpointRow"]>(
      `/api/v1/endpoints/${enc(id)}`,
      {
        method: "PUT",
      },
    );
  }

  async deleteEndpoint(id: string): Promise<string> {
    return this.request<string>(`/api/v1/endpoints/${enc(id)}`, {
      method: "DELETE",
    });
  }

  // Targets
  async getTargets(): Promise<components["schemas"]["DbListTargetsRow"][]> {
    return this.request<components["schemas"]["DbListTargetsRow"][]>(
      "/api/v1/targets",
    );
  }

  async getTarget(
    id: string,
  ): Promise<components["schemas"]["DbGetTargetRow"]> {
    return this.request<components["schemas"]["DbGetTargetRow"]>(
      `/api/v1/targets/${enc(id)}`,
    );
  }

  async createTarget(
    params: components["schemas"]["DbCreateTargetParams"],
  ): Promise<components["schemas"]["DbGetTargetRow"]> {
    return this.request<components["schemas"]["DbGetTargetRow"]>(
      "/api/v1/targets",
      {
        method: "POST",
        body: params,
      },
    );
  }

  async updateTarget(
    id: string,
  ): Promise<components["schemas"]["DbGetTargetRow"]> {
    return this.request<components["schemas"]["DbGetTargetRow"]>(
      `/api/v1/targets/${enc(id)}`,
      {
        method: "PUT",
      },
    );
  }

  async deleteTarget(id: string): Promise<string> {
    return this.request<string>(`/api/v1/targets/${enc(id)}`, {
      method: "DELETE",
    });
  }

  // Target Types
  async getTargetTypes(): Promise<components["schemas"]["DbTargetType"][]> {
    return this.request<components["schemas"]["DbTargetType"][]>(
      "/api/v1/target_types",
    );
  }

  async getTargetType(
    id: string,
  ): Promise<components["schemas"]["DbTargetType"]> {
    return this.request<components["schemas"]["DbTargetType"]>(
      `/api/v1/target_types/${enc(id)}`,
    );
  }

  async createTargetType(
    params: components["schemas"]["DbCreateTargetTypeParams"],
  ): Promise<components["schemas"]["DbTargetType"]> {
    return this.request<components["schemas"]["DbTargetType"]>(
      "/api/v1/target_types",
      {
        method: "POST",
        body: params,
      },
    );
  }

  async updateTargetType(
    id: string,
  ): Promise<components["schemas"]["DbTargetType"]> {
    return this.request<components["schemas"]["DbTargetType"]>(
      `/api/v1/target_types/${enc(id)}`,
      {
        method: "PUT",
      },
    );
  }

  async deleteTargetType(id: string): Promise<string> {
    return this.request<string>(`/api/v1/target_types/${enc(id)}`, {
      method: "DELETE",
    });
  }

  // Tenants
  async getTenants(): Promise<components["schemas"]["DbTenant"][]> {
    return this.request<components["schemas"]["DbTenant"][]>("/api/v1/tenants");
  }

  async getTenant(id: string): Promise<components["schemas"]["DbTenant"]> {
    return this.request<components["schemas"]["DbTenant"]>(
      `/api/v1/tenants/${enc(id)}`,
    );
  }

  async createTenant(
    params: components["schemas"]["DbCreateTenantParams"],
  ): Promise<components["schemas"]["OpenapigenCreateTenantResponse"]> {
    return this.request<
      components["schemas"]["OpenapigenCreateTenantResponse"]
    >("/api/v1/tenants", {
      method: "POST",
      body: params,
    });
  }

  async updateTenant(
    id: string,
    params: components["schemas"]["DbTenant"],
  ): Promise<components["schemas"]["DbTenant"]> {
    return this.request<components["schemas"]["DbTenant"]>(
      `/api/v1/tenants/${enc(id)}`,
      {
        method: "PUT",
        body: params,
      },
    );
  }

  async deleteTenant(
    id: string,
    options?: { cascade?: boolean; delete_raw_data?: boolean },
  ): Promise<string> {
    return this.request<string>(`/api/v1/tenants/${enc(id)}`, {
      method: "DELETE",
      params: options,
    });
  }

  async getOnboardingStatus(
    tenantId: string,
  ): Promise<components["schemas"]["OpenapigenOnboardingStatusResponse"]> {
    return this.request<
      components["schemas"]["OpenapigenOnboardingStatusResponse"]
    >(`/api/v1/tenants/${enc(tenantId)}/onboarding/status`);
  }

  async bootstrapTenant(
    tenantId: string,
    params: components["schemas"]["OpenapigenBootstrapTenantRequest"],
  ): Promise<components["schemas"]["OpenapigenBootstrapResult"]> {
    return this.request<components["schemas"]["OpenapigenBootstrapResult"]>(
      `/api/v1/tenants/${enc(tenantId)}/bootstrap`,
      {
        method: "POST",
        body: params,
      },
    );
  }

  async setTenantBootstrapAdmin(
    tenantId: string,
    params: components["schemas"]["OpenapigenSetBootstrapAdminRequest"],
  ): Promise<components["schemas"]["OpenapigenSetBootstrapAdminResponse"]> {
    return this.request<
      components["schemas"]["OpenapigenSetBootstrapAdminResponse"]
    >(`/api/v1/tenants/${enc(tenantId)}/bootstrap-admin`, {
      method: "PUT",
      body: params,
    });
  }

  async getTenantBootstrapOperations(
    tenantId: string,
  ): Promise<components["schemas"]["DbBootstrapOperation"][]> {
    return this.request<components["schemas"]["DbBootstrapOperation"][]>(
      `/api/v1/tenants/${enc(tenantId)}/bootstrap/operations`,
    );
  }

  async getTenantBootstrapOperation(
    tenantId: string,
    opId: string,
  ): Promise<components["schemas"]["DbBootstrapOperation"]> {
    return this.request<components["schemas"]["DbBootstrapOperation"]>(
      `/api/v1/tenants/${enc(tenantId)}/bootstrap/operations/${enc(opId)}`,
    );
  }

  async getTenantBootstrapUpdates(
    tenantId: string,
  ): Promise<components["schemas"]["OpenapigenBootstrapUpdatesResponse"]> {
    return this.request<
      components["schemas"]["OpenapigenBootstrapUpdatesResponse"]
    >(`/api/v1/tenants/${enc(tenantId)}/bootstrap/updates`);
  }

  buildLinkTenantVCSUrl(
    tenantId: string,
    vcsId: string,
    redirectUri?: string,
  ): string {
    return this.buildURL(
      `/api/v1/tenants/${enc(tenantId)}/link/${enc(vcsId)}`,
      redirectUri ? { redirect_uri: redirectUri } : undefined,
    );
  }

  // Config
  async getWebRTCConfig(): Promise<
    components["schemas"]["ConfigWebRTCConfig"]
  > {
    return this.request<components["schemas"]["ConfigWebRTCConfig"]>(
      "/api/v1/config/infra/webrtc",
    );
  }

  async getICEConfig(params?: {
    username?: string;
    ttl?: string;
  }): Promise<components["schemas"]["OpenapigenRTCConfiguration"]> {
    return this.request<components["schemas"]["OpenapigenRTCConfiguration"]>(
      "/api/v1/config/infra/ice",
      {
        params,
      },
    );
  }

  // VCS Providers
  async getVCSProviders(
    tenantId: string,
  ): Promise<components["schemas"]["OpenapigenVCSProviderResponse"][]> {
    return this.request<
      components["schemas"]["OpenapigenVCSProviderResponse"][]
    >(`/api/v1/tenants/${enc(tenantId)}/vcs-providers`);
  }

  async getVCSProvider(
    tenantId: string,
    vcsId: string,
  ): Promise<components["schemas"]["OpenapigenVCSProviderResponse"]> {
    return this.request<components["schemas"]["OpenapigenVCSProviderResponse"]>(
      `/api/v1/tenants/${enc(tenantId)}/vcs-providers/${enc(vcsId)}`,
    );
  }

  async createVCSProvider(
    tenantId: string,
    params: components["schemas"]["OpenapigenCreateVCSProviderParams"],
  ): Promise<components["schemas"]["OpenapigenVCSProviderResponse"]> {
    return this.request<components["schemas"]["OpenapigenVCSProviderResponse"]>(
      `/api/v1/tenants/${enc(tenantId)}/vcs-providers`,
      {
        method: "POST",
        body: params,
      },
    );
  }

  async updateVCSProvider(
    tenantId: string,
    vcsId: string,
    params: components["schemas"]["OpenapigenUpdateVCSProviderParams"],
  ): Promise<components["schemas"]["OpenapigenVCSProviderResponse"]> {
    return this.request<components["schemas"]["OpenapigenVCSProviderResponse"]>(
      `/api/v1/tenants/${enc(tenantId)}/vcs-providers/${enc(vcsId)}`,
      {
        method: "PUT",
        body: params,
      },
    );
  }

  async deleteVCSProvider(tenantId: string, vcsId: string): Promise<void> {
    return this.request<void>(
      `/api/v1/tenants/${enc(tenantId)}/vcs-providers/${enc(vcsId)}`,
      {
        method: "DELETE",
      },
    );
  }

  async getTenantCallbackURLs(
    tenantId: string,
    params: { name: string },
  ): Promise<
    components["schemas"]["OpenapigenGetTenantCallbackURLsResponse"]
  > {
    return this.request<
      components["schemas"]["OpenapigenGetTenantCallbackURLsResponse"]
    >(`/api/v1/tenants/${enc(tenantId)}/callback_urls`, { params });
  }

  async getTenantGitLabGroups(
    tenantId: string,
    params?: {
      vcs_provider_id?: string;
    },
  ): Promise<components["schemas"]["OpenapigenGitLabGroupResponse"][]> {
    return this.request<
      components["schemas"]["OpenapigenGitLabGroupResponse"][]
    >(`/api/v1/tenants/${enc(tenantId)}/gitlab/groups`, {
      params,
    });
  }

  async getTenantGitLabProjects(
    tenantId: string,
    params?: {
      vcs_provider_id?: string;
      group_id?: number;
    },
  ): Promise<components["schemas"]["OpenapigenGitLabProjectResponse"][]> {
    return this.request<
      components["schemas"]["OpenapigenGitLabProjectResponse"][]
    >(`/api/v1/tenants/${enc(tenantId)}/gitlab/projects`, {
      params,
    });
  }

  // Users
  async getUsers(params?: {
    email?: string;
  }): Promise<components["schemas"]["DbUser"][]> {
    return this.request<components["schemas"]["DbUser"][]>("/api/v1/users", {
      params,
    });
  }

  async updateUser(
    id: string,
    params: components["schemas"]["OpenapigenUpdateUserRequest"],
  ): Promise<components["schemas"]["DbUser"]> {
    return this.request<components["schemas"]["DbUser"]>(
      `/api/v1/users/${enc(id)}`,
      {
        method: "PUT",
        body: params,
      },
    );
  }

  // Feedback
  async submitFeedback(
    params: components["schemas"]["OpenapigenFeedbackRequest"],
  ): Promise<string> {
    return this.request<string>("/api/v1/feedback/mb", {
      method: "POST",
      body: params,
    });
  }

  // Auth
  async whoami(): Promise<components["schemas"]["OpenapigenWhoamiResponse"]> {
    return this.request<components["schemas"]["OpenapigenWhoamiResponse"]>(
      "/api/v1/whoami",
    );
  }

  // Permissions
  async getMyPermissions(): Promise<
    components["schemas"]["OpenapigenPermissionsSummary"]
  > {
    return this.request<
      components["schemas"]["OpenapigenPermissionsSummary"]
    >("/api/v1/permissions/me");
  }

  async getUserPermissions(
    userId: string,
  ): Promise<components["schemas"]["OpenapigenPermissionsSummary"]> {
    return this.request<
      components["schemas"]["OpenapigenPermissionsSummary"]
    >(`/api/v1/users/${enc(userId)}/permissions`);
  }

  async checkPermission(params: {
    relation: string;
    object: string;
  }): Promise<Record<string, boolean>> {
    return this.request<Record<string, boolean>>(
      "/api/v1/permissions/check",
      { params },
    );
  }

  async batchCheckPermissions(
    params: components["schemas"]["OpenapigenBatchCheckRequest"],
  ): Promise<components["schemas"]["OpenapigenBatchCheckResult"][]> {
    return this.request<
      components["schemas"]["OpenapigenBatchCheckResult"][]
    >("/api/v1/permissions/check", {
      method: "POST",
      body: params,
    });
  }

  // Global Admins
  async getAdmins(): Promise<
    components["schemas"]["OpenapigenAdminEntry"][]
  > {
    return this.request<components["schemas"]["OpenapigenAdminEntry"][]>(
      "/api/v1/admins",
    );
  }

  async addAdmin(
    params: components["schemas"]["OpenapigenAddAdminRequest"],
  ): Promise<string> {
    return this.request<string>("/api/v1/admins", {
      method: "POST",
      body: params,
    });
  }

  async removeAdmin(subject: string): Promise<string> {
    return this.request<string>(`/api/v1/admins/${enc(subject)}`, {
      method: "DELETE",
    });
  }

  // Groups
  async getTenantGroups(
    tenantId: string,
  ): Promise<components["schemas"]["DbGroup"][]> {
    return this.request<components["schemas"]["DbGroup"][]>(
      `/api/v1/tenants/${enc(tenantId)}/groups`,
    );
  }

  async createGroup(
    tenantId: string,
    params: components["schemas"]["OpenapigenCreateGroupRequest"],
  ): Promise<components["schemas"]["DbGroup"]> {
    return this.request<components["schemas"]["DbGroup"]>(
      `/api/v1/tenants/${enc(tenantId)}/groups`,
      {
        method: "POST",
        body: params,
      },
    );
  }

  async getGroup(id: string): Promise<components["schemas"]["DbGroup"]> {
    return this.request<components["schemas"]["DbGroup"]>(
      `/api/v1/groups/${enc(id)}`,
    );
  }

  async updateGroup(
    id: string,
    params: components["schemas"]["OpenapigenUpdateGroupRequest"],
  ): Promise<components["schemas"]["DbGroup"]> {
    return this.request<components["schemas"]["DbGroup"]>(
      `/api/v1/groups/${enc(id)}`,
      {
        method: "PUT",
        body: params,
      },
    );
  }

  async deleteGroup(id: string): Promise<string> {
    return this.request<string>(`/api/v1/groups/${enc(id)}`, {
      method: "DELETE",
    });
  }

  // Group Members
  async getGroupMembers(
    groupId: string,
  ): Promise<components["schemas"]["OpenapigenGroupMemberEntry"][]> {
    return this.request<
      components["schemas"]["OpenapigenGroupMemberEntry"][]
    >(`/api/v1/groups/${enc(groupId)}/members`);
  }

  async addGroupMember(
    groupId: string,
    params: components["schemas"]["OpenapigenAddGroupMemberRequest"],
  ): Promise<string> {
    return this.request<string>(`/api/v1/groups/${enc(groupId)}/members`, {
      method: "POST",
      body: params,
    });
  }

  async removeGroupMember(
    groupId: string,
    userId: string,
  ): Promise<string> {
    return this.request<string>(
      `/api/v1/groups/${enc(groupId)}/members/${enc(userId)}`,
      { method: "DELETE" },
    );
  }

  // Group Grants
  async getGroupGrants(
    groupId: string,
  ): Promise<components["schemas"]["OpenapigenGroupGrantEntry"][]> {
    return this.request<
      components["schemas"]["OpenapigenGroupGrantEntry"][]
    >(`/api/v1/groups/${enc(groupId)}/grants`);
  }

  async addGroupGrant(
    groupId: string,
    params: components["schemas"]["OpenapigenAddGroupGrantRequest"],
  ): Promise<string> {
    return this.request<string>(`/api/v1/groups/${enc(groupId)}/grants`, {
      method: "POST",
      body: params,
    });
  }

  async removeGroupGrant(
    groupId: string,
    resourceType: string,
    resourceId: string,
  ): Promise<string> {
    return this.request<string>(
      `/api/v1/groups/${enc(groupId)}/grants/${enc(resourceType)}/${enc(resourceId)}`,
      { method: "DELETE" },
    );
  }

  // Tenant Members
  async getTenantMembers(
    tenantId: string,
  ): Promise<components["schemas"]["OpenapigenTenantMemberEntry"][]> {
    return this.request<
      components["schemas"]["OpenapigenTenantMemberEntry"][]
    >(`/api/v1/tenants/${enc(tenantId)}/members`);
  }

  async addTenantMember(
    tenantId: string,
    params: components["schemas"]["OpenapigenAddTenantMemberRequest"],
  ): Promise<string> {
    return this.request<string>(`/api/v1/tenants/${enc(tenantId)}/members`, {
      method: "POST",
      body: params,
    });
  }

  async removeTenantMember(
    tenantId: string,
    userId: string,
  ): Promise<string> {
    return this.request<string>(
      `/api/v1/tenants/${enc(tenantId)}/members/${enc(userId)}`,
      { method: "DELETE" },
    );
  }

  // Resource Access (resource-centric grant management)
  async getResourceAccess(
    resourceType: string,
    resourceId: string,
  ): Promise<components["schemas"]["OpenapigenResourceAccessList"]> {
    return this.request<components["schemas"]["OpenapigenResourceAccessList"]>(
      `/api/v1/access/${enc(resourceType)}/${enc(resourceId)}`,
    );
  }

  async setResourceAccess(
    resourceType: string,
    resourceId: string,
    params: components["schemas"]["OpenapigenSetResourceAccessRequest"],
  ): Promise<void> {
    await this.request<void>(
      `/api/v1/access/${enc(resourceType)}/${enc(resourceId)}`,
      {
        method: "POST",
        body: params,
      },
    );
  }

  async removeResourceAccess(
    resourceType: string,
    resourceId: string,
    subjectType: string,
    subjectId: string,
  ): Promise<void> {
    await this.request<void>(
      `/api/v1/access/${enc(resourceType)}/${enc(resourceId)}/${enc(subjectType)}/${enc(subjectId)}`,
      { method: "DELETE" },
    );
  }

  // Redesign additions

  async getEmulatorStatus(
    id: string,
  ): Promise<components["schemas"]["OpenapigenEmulatorDeploymentStatus"]> {
    return this.request<components["schemas"]["OpenapigenEmulatorDeploymentStatus"]>(
      `/api/v1/emulators/${enc(id)}/status`,
    );
  }

  /** Swap an emulator onto a newer snapshot ("Update to latest"). */
  async updateEmulatorSnapshot(
    id: string,
    snapshotName: string,
  ): Promise<components["schemas"]["OpenapigenUpdateEmulatorSnapshotResponse"]> {
    return this.request<components["schemas"]["OpenapigenUpdateEmulatorSnapshotResponse"]>(
      `/api/v1/emulators/${enc(id)}/snapshot`,
      { method: "PUT", body: { snapshot_name: snapshotName } },
    );
  }

  async getReadiness(): Promise<components["schemas"]["HealthReport"]> {
    return this.request<components["schemas"]["HealthReport"]>("/-/readyz");
  }

  /** Global admin only. Served from ctrl's informer cache; 503 until synced. */
  async getPlatformCluster(): Promise<components["schemas"]["K8SClusterReport"]> {
    return this.request<components["schemas"]["K8SClusterReport"]>("/api/v1/platform/cluster");
  }

  async getRepoLocks(
    tenantId: string,
  ): Promise<components["schemas"]["OpenapigenRepoLockListEntry"][]> {
    return this.request<components["schemas"]["OpenapigenRepoLockListEntry"][]>(
      `/api/v1/tenants/${enc(tenantId)}/repolocks`,
    );
  }

  async getUserVCSCredentials(
    userId: string,
  ): Promise<components["schemas"]["OpenapigenUserVCSCredentialResponse"][]> {
    return this.request<components["schemas"]["OpenapigenUserVCSCredentialResponse"][]>(
      `/api/v1/users/${enc(userId)}/vcs-credentials`,
    );
  }

  async deleteUserVCSCredential(userId: string, credId: string): Promise<void> {
    await this.request<void>(
      `/api/v1/users/${enc(userId)}/vcs-credentials/${enc(credId)}`,
      { method: "DELETE" },
    );
  }

  async getAccountLinkURL(): Promise<components["schemas"]["OpenapigenAccountLinkURLResponse"]> {
    return this.request<components["schemas"]["OpenapigenAccountLinkURLResponse"]>(
      "/api/v1/account/link-url",
    );
  }

  async getGitLabJob(
    url: string,
  ): Promise<components["schemas"]["OpenapigenGitLabJobResponse"]> {
    return this.request<components["schemas"]["OpenapigenGitLabJobResponse"]>(
      "/api/v1/vcs/gitlab/job",
      { params: { url } },
    );
  }

  async getTenantByVCSURL(
    url: string,
  ): Promise<components["schemas"]["OpenapigenGetTenantByVCSURLResponse"]> {
    return this.request<components["schemas"]["OpenapigenGetTenantByVCSURLResponse"]>(
      "/api/v1/vcs/tenant",
      { params: { url } },
    );
  }

  // Runtime settings (system < tenant < group < user)
  async listSettingDefinitions(): Promise<components["schemas"]["SettingsDefinition"][]> {
    return this.request<components["schemas"]["SettingsDefinition"][]>("/api/v1/settings");
  }

  /** The caller's resolved values, each with the scope it came from. */
  async getMyEffectiveSettings(): Promise<components["schemas"]["OpenapigenEffectiveSettings"]> {
    return this.request<components["schemas"]["OpenapigenEffectiveSettings"]>("/api/v1/settings/effective");
  }

  async getUserEffectiveSettings(
    userId: string,
  ): Promise<components["schemas"]["OpenapigenEffectiveSettings"]> {
    return this.request<components["schemas"]["OpenapigenEffectiveSettings"]>(
      `/api/v1/users/${enc(userId)}/settings/effective`,
    );
  }

  /** Overrides stored at one scope; `id` is ignored for system scope. */
  async listScopeSettings(
    scope: SettingScope,
    id?: string,
  ): Promise<components["schemas"]["OpenapigenSettingOverride"][]> {
    return this.request<components["schemas"]["OpenapigenSettingOverride"][]>(settingsScopePath(scope, id));
  }

  async putScopeSetting(
    scope: SettingScope,
    id: string | undefined,
    key: string,
    value: unknown,
  ): Promise<components["schemas"]["OpenapigenSettingOverride"]> {
    return this.request<components["schemas"]["OpenapigenSettingOverride"]>(
      `${settingsScopePath(scope, id)}/${enc(key)}`,
      { method: "PUT", body: { value } },
    );
  }

  /** Removes the override so the value falls back to the next scope; 404 when none exists. */
  async deleteScopeSetting(scope: SettingScope, id: string | undefined, key: string): Promise<void> {
    await this.request<void>(`${settingsScopePath(scope, id)}/${enc(key)}`, { method: "DELETE" });
  }

  // Tenant tool distribution
  async getTenantToolSettings(
    tenantId: string,
  ): Promise<components["schemas"]["OpenapigenTenantToolSettings"]> {
    return this.request<components["schemas"]["OpenapigenTenantToolSettings"]>(
      `/api/v1/tenants/${enc(tenantId)}/tools/settings`,
    );
  }

  /** Replaces the whole settings document; every `default` field is required. */
  async updateTenantToolSettings(
    tenantId: string,
    params: components["schemas"]["OpenapigenPutTenantToolSettingsOp"],
  ): Promise<components["schemas"]["OpenapigenTenantToolSettings"]> {
    return this.request<components["schemas"]["OpenapigenTenantToolSettings"]>(
      `/api/v1/tenants/${enc(tenantId)}/tools/settings`,
      { method: "PUT", body: params },
    );
  }

  async getTenantTools(
    tenantId: string,
  ): Promise<components["schemas"]["OpenapigenTenantToolList"]> {
    return this.request<components["schemas"]["OpenapigenTenantToolList"]>(
      `/api/v1/tenants/${enc(tenantId)}/tools`,
    );
  }

  /** Omit `version` to adopt the tool's available version. */
  async adoptTenantTool(
    tenantId: string,
    tool: string,
    params: components["schemas"]["OpenapigenAdoptToolOp"] = {},
  ): Promise<components["schemas"]["OpenapigenTenantTool"]> {
    return this.request<components["schemas"]["OpenapigenTenantTool"]>(
      `/api/v1/tenants/${enc(tenantId)}/tools/${enc(tool)}/adopt`,
      { method: "POST", body: params },
    );
  }

  /** `tenant` is required when the caller belongs to several tenants. */
  async getToolVersions(
    tenantId?: string,
  ): Promise<components["schemas"]["OpenapigenToolVersions"]> {
    return this.request<components["schemas"]["OpenapigenToolVersions"]>(
      "/api/v1/tools/versions",
      { params: tenantId ? { tenant: tenantId } : undefined },
    );
  }

  /**
   * One page of a tool's releases, newest first; pass `next_cursor` back as
   * `cursor`. `tenant` is required when the caller belongs to several tenants.
   */
  async listToolReleases(
    tool: string,
    query: { limit?: number; cursor?: string; tenant?: string } = {},
  ): Promise<components["schemas"]["OpenapigenToolReleaseList"]> {
    const params: Record<string, string | number> = {};
    if (query.limit) params.limit = query.limit;
    if (query.cursor) params.cursor = query.cursor;
    if (query.tenant) params.tenant = query.tenant;
    return this.request<components["schemas"]["OpenapigenToolReleaseList"]>(
      `/api/v1/tools/${enc(tool)}/releases`,
      { params },
    );
  }

  /** `tenant` is required when the caller belongs to several tenants. */
  async getToolRelease(
    tool: string,
    version: string,
    query: { tenant?: string } = {},
  ): Promise<components["schemas"]["OpenapigenToolRelease"]> {
    return this.request<components["schemas"]["OpenapigenToolRelease"]>(
      `/api/v1/tools/${enc(tool)}/releases/${enc(version)}`,
      { params: query.tenant ? { tenant: query.tenant } : {} },
    );
  }

  /**
   * Mints a short-lived signed URL; `platform` is `os-arch` (or `os-arch.sig`).
   * `tenant` is required when the caller belongs to several tenants.
   */
  async downloadToolRelease(
    tool: string,
    version: string,
    params: components["schemas"]["OpenapigenDownloadToolReleaseOp"],
    query: { tenant?: string } = {},
  ): Promise<components["schemas"]["OpenapigenToolDownload"]> {
    return this.request<components["schemas"]["OpenapigenToolDownload"]>(
      `/api/v1/tools/${enc(tool)}/releases/${enc(version)}/download`,
      { method: "POST", body: params, params: query.tenant ? { tenant: query.tenant } : {} },
    );
  }
}
